import React, { useEffect, useState } from "react";
import "./Administration.css";
import { Button, Col, Popover, Switch, Tag, Tooltip, Typography } from "antd";

import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import CustomTableWithPagination from "../../components/CustomTableWithPagination/CustomTableWithPagination";
import SearchInput from "../../components/Input/SearchInputs/SearchInput";
// import { ReactComponent as DefaultIcon } from "../../assets/icons/default.svg";

import {
  useLazyGetAdminUsersQuery,
  useCreateAdminUserMutation,
  useUpdateAdminUserMutation,
  useDeleteAdminUserMutation,
  useUpdateAdminUserStatusMutation,
} from "../../apis/userManagement";
import { useLazyGetServiceableCitiesQuery } from "../../apis/utilities";

// import { baseUrl } from "../../commonUtils/commonUtils";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import ManageDeleteModal from "../../components/Modal/DeleteModal/ManageDeleteModal/ManageDeleteModal";
import StatusModal from "../../components/Modal/StatusModal/StatusModal";
import {
  showErrorToast,
  showSuccessToast,
} from "../../NotificationToast/NotificationToast";
import NoDataFound from "../../components/EmptyTable/NoDataFound";
import AdminUserModal from "./AdminUserModal";
const { Text } = Typography;

const Administration = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState({});
  const [searchedValue, setSearchedValue] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [
    getAllAdminUsersApi,
    { data: tableData, isFetching },
  ] = useLazyGetAdminUsersQuery();
  const [getServiceableCitiesApi, { data: locationsData, isFetching: isCityLoading }] =
    useLazyGetServiceableCitiesQuery();
  const [createAdminUserApi, { isLoading: isCreating }] =
    useCreateAdminUserMutation();
  const [updateAdminUserApi, { isLoading: isUpdating }] =
    useUpdateAdminUserMutation();
  const [deleteAdminUserApi] = useDeleteAdminUserMutation();
  const [updateAdminUserStatusApi] = useUpdateAdminUserStatusMutation();

  const cityOptions = (locationsData?.data || []).map((item) => ({
    label: item.city,
    value: item.city,
  }));

  useEffect(() => {
    getAllAdminUsersApi();
    getServiceableCitiesApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showApiError = (response) => {
    const errorData = response?.error?.data;
    const fieldErrors = errorData?.data;
    const messages =
      fieldErrors && typeof fieldErrors === "object"
        ? Object.values(fieldErrors).flat()
        : [];
    if (messages.length) {
      showErrorToast(
        <>
          {messages.map((msg, index) => (
            <div key={index}>{msg}</div>
          ))}
        </>
      );
    } else {
      showErrorToast(errorData?.message);
    }
  };

  const handleAddAdminUser = () => {
    setIsAddModalOpen(true);
  };

  const onEdit = (data) => {
    setSelectedRowData(data);
    setIsEditModalOpen(true);
  };

  const handleAddEditCancel = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
  };

  const handleAddEditSubmit = async (values, profileImageFile) => {
    const params = {
      ...values,
      ...(profileImageFile && { profile_image: profileImageFile }),
    };
    const response = isEditModalOpen
      ? await updateAdminUserApi({ id: selectedRowData?.id, ...params })
      : await createAdminUserApi(params);
    if (response?.data?.status) {
      showSuccessToast(response?.data?.message);
      handleAddEditCancel();
      getAllAdminUsersApi();
    } else {
      showApiError(response);
    }
  };

  const handleDeleteModalOpen = (data) => {
    setSelectedRowData(data);
    setIsDeleteModalOpen(true);
  };
  const handleDeleteModalCancel = () => {
    setIsDeleteModalOpen(false);
  };
  const onDelete = async () => {
    const response = await deleteAdminUserApi(selectedRowData?.id);
    if (response?.data?.status) {
      showSuccessToast(response?.data?.message);
      setIsDeleteModalOpen(false);
      getAllAdminUsersApi();
    } else {
      showApiError(response);
    }
  };

  const handleStatus = (data) => {
    setSelectedRowData(data);
    setIsStatusModalOpen(true);
  };
  const onStatusChange = async () => {
    const response = await updateAdminUserStatusApi({
      id: selectedRowData?.id,
      is_active: !selectedRowData?.is_active,
    });
    if (response?.data?.status) {
      showSuccessToast(response?.data?.message);
      setIsStatusModalOpen(false);
      getAllAdminUsersApi();
    } else {
      showApiError(response);
    }
  };

  const handleSearchedValue = (params) => {
    setSearchedValue(params);
    setCurrentPage(1);
  };

  const handlePageChange = async (currPage) => {
    const params = {
      page: currPage,
      ...(searchedValue && { search: searchedValue?.search }),
    };
    setCurrentPage(currPage);
    await getAllAdminUsersApi(params);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "full_name",
      key: "full_name",
      fixed: "left",
      width: "150px",
      render: (params, record) => {
        return (
          <Col
            style={{
              display: "flex",
              gap: "20px",
              alignItems: "center",
            }}
          >
            {/* <Avatar
              style={{
                width: "48px",
                height: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f1f1f2",
              }}
              shape="square"
              src={record?.profile_image && `${baseUrl}${record.profile_image}`}
              alt="Avatar"
              icon={<DefaultIcon width={20} height={20} />}
            /> */}
            <Text>{record?.full_name}</Text>
          </Col>
        );
      },
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: "200px",
    },
    {
      title: "Phone number",
      dataIndex: "phone_number",
      key: "phone_number",
      width: "150px",
    },
    // {
    //   title: "Email",
    //   dataIndex: "email",
    //   key: "email",
    //   width: "250px",
    // },
    {
      title: "City Access",
      dataIndex: "city",
      key: "city",
      width: "250px",
      render: (params, record) => {
        const cities = record?.city || [];
        const visibleCities = cities.slice(0, 2);
        const remainingCount = cities.length - visibleCities.length;
        if (!cities.length) {
          return <Text>-</Text>;
        }
        return (
          <Col style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {visibleCities.map((city) => (
              <Tag key={city} color="blue">
                {city}
              </Tag>
            ))}
            {remainingCount > 0 && (
              <Popover
                title={`City Access (${cities.length})`}
                trigger={["hover", "click"]}
                content={
                  <Col
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      maxHeight: "220px",
                      overflowY: "auto",
                      minWidth: "160px",
                    }}
                  >
                    {cities.map((city) => (
                      <Text key={city}>{city}</Text>
                    ))}
                  </Col>
                }
              >
                <Tag className="city-more-tag">{`+${remainingCount}`}</Tag>
              </Popover>
            )}
          </Col>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: "100px",
      render: (params, record) => {
        return (
          <Tooltip
            title={record?.is_active ? "Active" : "Inactive"}
            color="white"
            overlayInnerStyle={{ color: "#000" }}
          >
            <Switch
              checked={!!record?.is_active}
              onChange={() => handleStatus(record)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "100px",
      render: (params, record) => {
        return (
          <Col style={{ display: "flex", gap: "10px" }}>
            <Tooltip title="Edit" color="white" overlayInnerStyle={{ color: "#000" }}>
              <EditOutlined
                className="action-icon action-icon-edit"
                onClick={() => onEdit(record)}
              />
            </Tooltip>
            <Tooltip title="Delete" color="white" overlayInnerStyle={{ color: "#000" }}>
              <DeleteOutlined
                className="action-icon action-icon-delete"
                onClick={() => handleDeleteModalOpen(record)}
              />
            </Tooltip>
          </Col>
        );
      },
    },
  ];
  return (
    <Col className="customer-container">
      <Text className="customer_title">Admin Users</Text>
      <Col style={{ display: "flex", justifyContent: "space-between" }}>
        <SearchInput
          size={"large"}
          placeholder={"Search by name, username, phone"}
          getLibraryApi={getAllAdminUsersApi}
          width={"400px"}
          isLoading={isFetching}
          handleSearchedValue={handleSearchedValue}
        />
        <Button
          type="primary"
          className="add_customer_btn"
          onClick={handleAddAdminUser}
        >
          Add admin user
        </Button>
      </Col>
      <CustomTableWithPagination
        className="order-list-table"
        tableDataSource={tableData?.data?.results}
        tableColumns={columns}
        rowKey={"id"}
        locale={{
          emptyText: isFetching ? (
            <TableSkeleton length={10} />
          ) : (
            <NoDataFound text={"No admin users found!"} />
          ),
        }}
        isLoading={tableData?.data?.results?.length && isFetching}
        scroll={{ x: "100%" }}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        itemsPerPage={30}
        totalEntries={!isFetching && tableData?.pagination?.total_entries}
      />
      <AdminUserModal
        open={isAddModalOpen || isEditModalOpen}
        isEditMode={isEditModalOpen}
        initialValues={selectedRowData}
        cityOptions={cityOptions}
        isCityLoading={isCityLoading}
        isSubmitting={isCreating || isUpdating}
        onCancel={handleAddEditCancel}
        onSubmit={handleAddEditSubmit}
      />
      <ManageDeleteModal
        handleOk={handleDeleteModalCancel}
        open={isDeleteModalOpen}
        handleSave={onDelete}
        data={selectedRowData?.full_name}
      />
      <StatusModal
        open={isStatusModalOpen}
        setOpen={setIsStatusModalOpen}
        handleOk={onStatusChange}
        data={{ ...selectedRowData, vendor_name: selectedRowData?.full_name }}
      />
    </Col>
  );
};

export default Administration;
